// Central site configuration: brand, navigation, contact.
// Edit here to update the header, footer, and metadata everywhere.

export const site = {
  name: "Table Space",
  legalName: "Tablespace Technologies",
  tagline: "BIG needs BOLD.",
  description:
    "We reimagine the boundaries of what's possible with workspaces — powering the BIG ambitions of enterprises and GCCs with the boldness of our reimagination. From Workspaces to BOLDspaces.",
  // Apex is canonical: all 281 indexed old-site URLs are apex, and the live
  // nginx 301s www → apex. Sitemap/canonical/OG URLs must not redirect.
  url: process.env.NEXT_PUBLIC_SITE_URL ?? "https://tablespace.com",
  foundingYear: "2017",
  contact: {
    email: "info@tablespace.com",
    sales: "sales@tablespace.com",
    phone: "1800 315 7871",
    address: "5th Floor, Prestige Trade Tower, Palace Rd, High Grounds, Sampangirama Nagar, Bengaluru, Karnataka - 560001",
  },
  // Statutory identity, as shown in the footer's Registered Office block and
  // on the investor disclosures. "Tablespace" is one word in the registered
  // name — that is how it is filed, and it differs from the brand spelling.
  registeredOffice: {
    name: "Tablespace Technologies Limited",
    cin: "U74999KA2017PLC101040",
    address: "5th Floor, Prestige Trade Tower, Palace Rd, High Grounds, Sampangirama Nagar, Bengaluru, Karnataka - 560001",
  },
  // Social profiles for schema.org sameAs (verify/replace with official handles).
  social: [
    "https://www.linkedin.com/company/tablespaceofficial/",
    "https://www.instagram.com/tablespaceofficial/",
    "https://www.facebook.com/TableSpaceOfficial",
    "https://x.com/Table_Space_",
    "https://youtube.com/@tablespaceofficial",
  ],
  // Cities served (for areaServed + GEO).
  cities: [
    "Bengaluru",
    "Hyderabad",
    "Mumbai",
    "Gurugram",
    "Noida",
    "New Delhi",
    "Chennai",
    "Pune",
  ],
  // Headline stats. The homepage stat band (components/sections/Hero.tsx) is
  // the source of truth; these mirror it. Nothing reads this block today —
  // keep it in step so it is safe to pick up.
  stats: {
    cities: 9,
    sqft: "11.46 Mn",
    centres: "80+",
    clients: "425+",
  },
} as const;

// Primary navigation. Items with `children` render as dropdowns.
export const mainNav: NavItem[] = [
  { label: "About", href: "/about" },
  {
    label: "Solutions",
    href: "/solutions",
    children: [
      { label: "Custom Built", href: "/solutions/custom-built" },
      { label: "Ready to Move in Suites", href: "/solutions/suites" },
      { label: "DESYN", href: "/solutions/desyn" },
      { label: "Enterprise Services", href: "/solutions/enterprise-services" },
      // Appended rather than inserted: the admin stores nav label overrides by
      // index (nav.N.sub.M.label), so putting this anywhere but the end would
      // slide every saved label onto the wrong item.
      { label: "Global Capability Centres", href: "/solutions/global-capability-center" },
    ],
  },
  {
    label: "Locations",
    href: "/locations",
    children: [
      { label: "Bengaluru", href: "/locations/bengaluru" },
      { label: "Hyderabad", href: "/locations/hyderabad" },
      { label: "Mumbai", href: "/locations/mumbai" },
      { label: "Gurugram", href: "/locations/gurugram" },
      { label: "Noida", href: "/locations/noida" },
      { label: "New Delhi", href: "/locations/delhi" },
      { label: "Chennai", href: "/locations/chennai" },
      { label: "Pune", href: "/locations/pune" },
    ],
  },
  { label: "Technology", href: "/technology" },
  { label: "Media", href: "/media" },
  { label: "Investor Relations", href: "/investor-relations" },
  // No "Contact Us" item: the Enquire now button beside the nav goes to the
  // same page, and two links to one destination in one bar is noise.
];

export type NavItem = {
  label: string;
  href: string;
  children?: NavItem[];
};


/** Display name → the city's real route slug.
 *
 * Slugifying the label is not enough: "New Delhi" would give /locations/new-delhi,
 * which 404s — the route is /locations/delhi. Anything not listed falls back to
 * a plain slug, which is correct for every other city.
 */
const CITY_SLUG_OVERRIDES: Record<string, string> = {
  "new delhi": "delhi",
};

export function citySlug(city: string): string {
  const key = city.trim().toLowerCase();
  return CITY_SLUG_OVERRIDES[key] ?? key.replace(/\s+/g, "-");
}
